import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { type CellContext, type ColumnDef } from "@tanstack/react-table";
import {
  CheckCircleIcon,
  ClockCircleIcon,
  CloseIcon,
  StickerIcon,
} from "vega-icons";

import { getFullName } from "@vega-api/custom/utils/user";
import {
  getGetDealShortlistQueryKey,
  useUpdateDealShortlistItem,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ClientAvatar } from "@vega-core/components/ClientAvatar/ClientAvatar";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { TABLE_COLUMN_WIDTHS } from "@vega-core/components/Table/CoreTable/constants";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { formatMoneyShort } from "@vega-core/utils/formatMoney";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { TIER_OPTIONS } from "../../../constants/prospectConstants";
import { type ProspectsListTableColumnId } from "../constants";

const prospectsTableActionButtonClassName =
  "box-border h-[35px] min-h-[35px] shrink-0 px-2.5 py-0";

export type ProspectsListColumnIds = `${ProspectsListTableColumnId}`;

type UseProspectsListTableColumnsParams = {
  dealId: string;
  canEdit: boolean;
  canApprove?: boolean;
  onApproveProspect?: (prospectId: string) => void;
  approvingProspectId?: string | null;
  onRemoveProspect?: (prospectId: string) => void;
  removingProspectId?: string | null;
  onOpenNotesModal?: (rowIndex: number) => void;
  onOpenEstMinModal: (item: DealShortlistItemResponse) => void;
  onOpenEstMaxModal: (item: DealShortlistItemResponse) => void;
};

export const useProspectsListTableColumns = ({
  dealId,
  canEdit,
  canApprove,
  onApproveProspect,
  approvingProspectId,
  onRemoveProspect,
  removingProspectId,
  onOpenNotesModal,
  onOpenEstMinModal,
  onOpenEstMaxModal,
}: UseProspectsListTableColumnsParams): ColumnDef<
  DealShortlistItemResponse,
  unknown
>[] => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: updateShortlistItem } = useUpdateDealShortlistItem({
    mutation: {
      onSuccess: () => {
        showToast({
          variant: "success",
          title: m.status_success(),
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.status_error(),
        });
      },
    },
  });

  const columns = useMemo(() => {
    const approvedColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "approved",
      header: "",
      size: TABLE_COLUMN_WIDTHS.SELECTION,
      maxSize: TABLE_COLUMN_WIDTHS.SELECTION,
      enableSorting: false,
      accessorKey: "isApproved",
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        return (
          <div className="flex items-center justify-center">
            {item.isApproved ? (
              <CheckCircleIcon
                aria-label={m.coInvestments_prospects_approved()}
                className="size-4 text-text/positive"
              />
            ) : (
              <ClockCircleIcon
                aria-label={m.common_pending()}
                className="size-4 text-text/secondary"
              />
            )}
          </div>
        );
      },
    };

    const limitedPartnerColumn: ColumnDef<DealShortlistItemResponse, unknown> =
      {
        id: "limitedPartner",
        maxSize: 420,
        header: m.coInvestments_prospects_limitedPartner(),
        accessorFn: (row) => row.limitedPartner.name,
        cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
          const item = info.row.original;

          return (
            <span className="body-base-medium truncate">
              {item.limitedPartner.name || ""}
            </span>
          );
        },
      };

    const actionsColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "actions",
      header: "",
      size: 280,
      maxSize: 280,
      addCellTooltip: false,
      enableSorting: false,
      isMobileAction: true,
      cell: ({
        row: {
          index: rowIndex,
          original: { id: prospectId, isApproved, canRemoveFromShortlist },
        },
      }: CellContext<DealShortlistItemResponse, unknown>) => {
        const isApproving = approvingProspectId === prospectId;
        const isRemoving = removingProspectId === prospectId;

        return (
          <div className="flex items-center gap-2">
            {canApprove && onApproveProspect && !isApproved ? (
              <ThemedButton
                className={prospectsTableActionButtonClassName}
                dataTestId={`approve-prospect-button-${rowIndex}`}
                disabled={isApproving}
                icon={<CheckCircleIcon className="size-4 text-text/positive" />}
                iconPosition="left"
                onClick={() => onApproveProspect(prospectId)}
                size="small"
                variant="secondary"
              >
                {m.coInvestments_prospects_approve()}
              </ThemedButton>
            ) : null}
            {onRemoveProspect && canRemoveFromShortlist ? (
              <ThemedButton
                ariaLabel={m.coInvestments_prospects_remove_button()}
                className={prospectsTableActionButtonClassName}
                dataTestId={`remove-prospect-button-${rowIndex}`}
                disabled={isRemoving}
                icon={<CloseIcon className="text-text/negative size-4" />}
                iconPosition="left"
                onClick={() => onRemoveProspect(prospectId)}
                size="small"
                tooltip={m.coInvestments_prospects_remove_button()}
                variant="secondary"
              />
            ) : null}
          </div>
        );
      },
    };

    const tierColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "tier",
      header: m.coInvestments_prospects_tier(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      maxSize: TABLE_COLUMN_WIDTHS.DEFAULT,
      accessorKey: "tier",
      accessorFn: (row) => {
        const num = Number(row.tier);

        return Number.isNaN(num) ? undefined : num;
      },
      sortUndefined: -1,
      addCellTooltip: false,
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        if (canEdit) {
          return (
            <ThemedDropdown
              buttonProps={{
                className:
                  "border-1 border-transparent hover:border-brand-hover rounded-small p-2",
              }}
              isClearable={false}
              name={`tier-${item.id}`}
              onChange={(option) => {
                if (!option || option.value === item.tier) return;
                updateShortlistItem({
                  dealId,
                  dealShortlistItemId: item.id,
                  data: { tierLevel: Number(option.value) },
                });
              }}
              options={TIER_OPTIONS}
              renderOption={(option) => (
                <ClientAvatar
                  isBold
                  name={option.label}
                  size="small"
                  variant="primaryBackground"
                />
              )}
              value={item.tier}
            />
          );
        }

        return (
          <ClientAvatar
            isBold
            name={item.tier || ""}
            size="small"
            variant="primaryBackground"
          />
        );
      },
    };

    const estimatedTicketSizeMinColumn: ColumnDef<
      DealShortlistItemResponse,
      unknown
    > = {
      id: "estimatedTicketSizeMin",
      header: m.coInvestments_prospects_estimatedTicketSizeMin(),
      accessorFn: (row) => row.estimatedTicketSizeMin?.amount,
      sortUndefined: -1,
      addCellTooltip: false,
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;
        const formatted = item.estimatedTicketSizeMin
          ? formatMoneyShort(item.estimatedTicketSizeMin)
          : "—";

        if (canEdit) {
          return (
            <button
              className="inline-flex border-1 border-transparent hover:border-brand-hover rounded-small p-2 body-base-regular"
              data-testid={`est-min-ticket-size-${item.id}`}
              onClick={() => onOpenEstMinModal(item)}
              type="button"
            >
              {formatted}
            </button>
          );
        }

        return <span className="body-base-regular">{formatted}</span>;
      },
    };

    const estimatedTicketSizeMaxColumn: ColumnDef<
      DealShortlistItemResponse,
      unknown
    > = {
      id: "estimatedTicketSizeMax",
      header: m.coInvestments_prospects_estimatedTicketSizeMax(),
      accessorFn: (row) => row.estimatedTicketSizeMax?.amount,
      sortUndefined: -1,
      addCellTooltip: false,
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;
        const formatted = item.estimatedTicketSizeMax
          ? formatMoneyShort(item.estimatedTicketSizeMax)
          : "—";

        if (canEdit) {
          return (
            <button
              className="inline-flex border-1 border-transparent hover:border-brand-hover rounded-small p-2 body-base-regular"
              data-testid={`est-max-ticket-size-${item.id}`}
              onClick={() => onOpenEstMaxModal(item)}
              type="button"
            >
              {formatted}
            </button>
          );
        }

        return <span className="body-base-regular">{formatted}</span>;
      },
    };

    const probabilityWeightedMidPointColumn: ColumnDef<
      DealShortlistItemResponse,
      unknown
    > = {
      id: "probabilityWeightedMidPoint",
      header: m.coInvestments_prospects_probabilityWeightedMidPoint(),
      accessorFn: (row) => row.probabilityWeightedMidPoint?.amount,
      sortUndefined: -1,
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        if (!item.probabilityWeightedMidPoint) {
          return <span className="body-base-regular">&mdash;</span>;
        }

        return (
          <span className="body-base-regular">
            {formatMoneyShort(item.probabilityWeightedMidPoint)}
          </span>
        );
      },
    };

    const icgPriorityColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "icgPriority",
      header: m.coInvestments_prospects_icgPriority(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      maxSize: TABLE_COLUMN_WIDTHS.DEFAULT,
      accessorKey: "icgPriority",
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        if (item.icgPriority == null) {
          return <span className="body-base-regular">&mdash;</span>;
        }

        return (
          <span className="body-base-regular">
            {item.icgPriority ? m.common_yes() : m.common_no()}
          </span>
        );
      },
    };

    const lpSmaColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "lpSma",
      header: m.coInvestments_prospects_lpSma(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      maxSize: TABLE_COLUMN_WIDTHS.DEFAULT,
      accessorKey: "lpSma",
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        if (item.lpSma == null) {
          return <span className="body-base-regular">&mdash;</span>;
        }

        return (
          <span className="body-base-regular">
            {item.lpSma ? m.common_yes() : m.common_no()}
          </span>
        );
      },
    };

    const organizationChannelColumn: ColumnDef<
      DealShortlistItemResponse,
      unknown
    > = {
      id: "organizationChannel",
      header: m.coInvestments_prospects_organizationChannel(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      accessorKey: "organizationChannel",
      accessorFn: (row) => row.organizationChannel || undefined,
      sortUndefined: -1,
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        if (!item.organizationChannel) {
          return <span className="body-base-regular">&mdash;</span>;
        }

        return (
          <span className="body-base-regular">{item.organizationChannel}</span>
        );
      },
    };

    const addedByColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "addedBy",
      header: m.coInvestments_prospects_addedBy(),
      accessorFn: (row) =>
        row.addedBy ? getFullName(row.addedBy.fullName) : "",
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const item = info.row.original;

        if (!item.addedBy) {
          return <span className="body-base-regular">&mdash;</span>;
        }

        const fullName = getFullName(item.addedBy.fullName);

        return (
          <div className="flex gap-1">
            <Avatar initials={fullName} />
            <div className="flex flex-col">
              <span className="text-text/primary body-small-medium">
                {fullName}
              </span>
              <span className="text-text/secondary body-small-regular">
                {item.addedBy.email}
              </span>
            </div>
          </div>
        );
      },
    };

    const notesColumn: ColumnDef<DealShortlistItemResponse, unknown> = {
      id: "notes",
      header: m.coInvestments_outreach_notes(),
      size: TABLE_COLUMN_WIDTHS.DEFAULT,
      enableSorting: false,
      cell: (info: CellContext<DealShortlistItemResponse, unknown>) => {
        const rowIndex = info.row.index;

        return onOpenNotesModal ? (
          <ThemedButton
            dataTestId={`notes-button-${rowIndex}`}
            icon={<StickerIcon className="w-4 h-4" />}
            iconPosition="left"
            onClick={() => onOpenNotesModal(rowIndex)}
            variant="text"
          >
            {m.coInvestments_outreach_notes()}
          </ThemedButton>
        ) : (
          <span className="body-base-regular">&mdash;</span>
        );
      },
    };

    return [
      approvedColumn,
      limitedPartnerColumn,
      actionsColumn,
      tierColumn,
      estimatedTicketSizeMinColumn,
      estimatedTicketSizeMaxColumn,
      probabilityWeightedMidPointColumn,
      icgPriorityColumn,
      lpSmaColumn,
      organizationChannelColumn,
      addedByColumn,
      notesColumn,
    ];
  }, [
    canEdit,
    canApprove,
    onApproveProspect,
    approvingProspectId,
    dealId,
    onRemoveProspect,
    removingProspectId,
    onOpenNotesModal,
    onOpenEstMinModal,
    onOpenEstMaxModal,
    updateShortlistItem,
  ]);

  return columns;
};
